import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Loader2,
  Percent,
  Scissors,
  Settings,
  User,
} from "lucide-react";
import type { ReactNode } from "react";
import type { IntentActionType, PayloadRecord, ToolResultRecord } from "./types";

type Translator = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;

export const getOperationName = (
  actionType: IntentActionType,
  payload: PayloadRecord,
  toolResult: ToolResultRecord | undefined,
  t: Translator
): string => {
  switch (actionType) {
    case "MANAGE_SERVICE":
      return toolResult?.action === "update" || typeof payload.serviceId === "string"
        ? t("intent.operations.manageService.update", "Update Service")
        : t("intent.operations.manageService.create", "Create Service");
    case "MANAGE_CATEGORY":
      return toolResult?.action === "update" || typeof payload.categoryId === "string"
        ? t("intent.operations.manageCategory.update", "Update Category")
        : t("intent.operations.manageCategory.create", "Create Category");
    case "SORT_ITEMS":
      return payload.type === "categories"
        ? t("intent.operations.sortCategories", "Sort Categories")
        : t("intent.operations.sortServices", "Sort Services");
    case "BATCH_UPDATE_SERVICES":
      return t("intent.operations.batchUpdateServices", "Batch Update Services");
    case "BULK_UPDATE_SERVICES":
      return t("intent.operations.bulkUpdateServices", "Bulk Update Services");
    case "COPY_SERVICES":
      return t("intent.operations.copyServices", "Copy Services");
    case "UPDATE_WORKER_SERVICES":
      return t("intent.operations.updateWorkerServices", "Update Worker Restrictions");
    case "BATCH_CREATE_SERVICES":
      return t("intent.operations.batchCreateServices", "Batch Create Services");
    case "BATCH_CREATE_CATEGORIES":
      return t("intent.operations.batchCreateCategories", "Batch Create Categories");
    case "BATCH_UPDATE_CATEGORIES":
      return t("intent.operations.batchUpdateCategories", "Batch Update Categories");
    case "COMMIT_CONFIG_CHANGE":
      return t("intent.operations.commitConfigChange", "Commit Config Change");
    case "UPDATE_OPERATING_HOURS":
      return t("intent.operations.updateOperatingHours", "Update Operating Hours");
    case "UPDATE_SALON_PROFILE":
      return t("intent.operations.updateSalonProfile", "Update Salon Profile");
    case "UPDATE_SALON_CONTACT":
      return t("intent.operations.updateSalonContact", "Update Salon Contact");
    case "CREATE_HOLIDAY":
      return t("intent.operations.createHoliday", "Create Holiday");
    case "UPDATE_HOLIDAY":
      return t("intent.operations.updateHoliday", "Update Holiday");
    case "CREATE_TEAM_MEMBER":
      return t("intent.operations.createTeamMember", "Create Team Member");
    case "UPDATE_TEAM_MEMBER":
      return t("intent.operations.updateTeamMember", "Update Team Member");
    case "UPDATE_WORKER_SERVICE_RESTRICTIONS":
      return t("intent.operations.updateWorkerServiceRestrictions", "Update Worker Restrictions");
    case "CREATE_APPOINTMENT":
      return t("intent.operations.createAppointment", "Create Appointment");
    case "UPDATE_APPOINTMENT":
      return t("intent.operations.updateAppointment", "Update Appointment");
    case "BATCH_UPDATE_APPOINTMENTS":
      return t("intent.operations.batchUpdateAppointments", "Batch Update Appointments");
    case "UPDATE_APPOINTMENT_STATUS": {
      const action = typeof payload.action === "string" ? payload.action : undefined;
      if (action === "cancel")
        return t("intent.operations.cancelAppointment", "Cancel Appointment");
      if (action === "complete")
        return t("intent.operations.completeAppointment", "Complete Appointment");
      return t("intent.operations.updateAppointmentStatus", "Update Appointment Status");
    }
    case "ASSIGN_APPOINTMENT_WORKER":
      return t("intent.operations.assignAppointmentWorker", "Assign Appointment Worker");
    case "CREATE_CUSTOMER":
      return t("intent.operations.createCustomer", "Create Customer");
    case "CREATE_DISCOUNT":
      return t("intent.operations.createDiscount", "Create Discount");
    case "UPDATE_DISCOUNT":
      return t("intent.operations.updateDiscount", "Update Discount");
    case "DELETE_DISCOUNT":
      return t("intent.operations.deleteDiscount", "Delete Discount");
    default:
      return t("intent.operations.generic", "Intent Operation");
  }
};

export const getOperationIcon = (
  actionType: IntentActionType,
  _payload: PayloadRecord,
  _toolResult?: ToolResultRecord
): ReactNode => {
  switch (actionType) {
    // Services domain
    case "MANAGE_SERVICE":
    case "MANAGE_CATEGORY":
    case "SORT_ITEMS":
    case "BATCH_UPDATE_SERVICES":
    case "BULK_UPDATE_SERVICES":
    case "COPY_SERVICES":
    case "UPDATE_WORKER_SERVICES":
    case "BATCH_CREATE_SERVICES":
    case "BATCH_CREATE_CATEGORIES":
    case "BATCH_UPDATE_CATEGORIES":
      return <Scissors className="h-4 w-4 text-blue-700" />;

    // Config domain
    case "COMMIT_CONFIG_CHANGE":
    case "UPDATE_OPERATING_HOURS":
    case "UPDATE_SALON_PROFILE":
    case "UPDATE_SALON_CONTACT":
    case "CREATE_HOLIDAY":
    case "UPDATE_HOLIDAY":
    case "CREATE_TEAM_MEMBER":
    case "UPDATE_TEAM_MEMBER":
    case "UPDATE_WORKER_SERVICE_RESTRICTIONS":
      return <Settings className="h-4 w-4 text-blue-700" />;

    // Appointments domain
    case "CREATE_APPOINTMENT":
    case "UPDATE_APPOINTMENT":
    case "BATCH_UPDATE_APPOINTMENTS":
    case "UPDATE_APPOINTMENT_STATUS":
    case "ASSIGN_APPOINTMENT_WORKER":
      return <Calendar className="h-4 w-4 text-blue-700" />;

    // Customers domain
    case "CREATE_CUSTOMER":
      return <User className="h-4 w-4 text-blue-700" />;

    // Discounts domain
    case "CREATE_DISCOUNT":
    case "UPDATE_DISCOUNT":
    case "DELETE_DISCOUNT":
      return <Percent className="h-4 w-4 text-blue-700" />;

    default:
      return <AlertCircle className="h-4 w-4 text-blue-700" />;
  }
};

export const getStatusMeta = (
  localStatus: "PENDING" | "EXECUTED" | "REJECTED" | "FAILED",
  isToolDone: boolean,
  labels: {
    approved: string;
    rejected: string;
    failed: string;
    creating: string;
    pending: string;
  }
) => {
  if (localStatus === "EXECUTED") {
    return {
      text: labels.approved,
      icon: <CheckCircle className="h-3.5 w-3.5 text-green-600" />,
      className:
        "border-green-200 bg-green-50 text-green-700 dark:border-green-900/40 dark:bg-green-950/30 dark:text-green-300",
    };
  }

  if (localStatus === "FAILED") {
    return {
      text: labels.failed,
      icon: <AlertCircle className="h-3.5 w-3.5 text-red-600" />,
      className:
        "border-red-200 bg-red-50 text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300",
    };
  }

  if (localStatus === "REJECTED") {
    return {
      text: labels.rejected,
      icon: <AlertCircle className="h-3.5 w-3.5 text-red-600" />,
      className:
        "border-red-200 bg-red-50 text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300",
    };
  }

  if (!isToolDone) {
    return {
      text: labels.creating,
      icon: <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />,
      className:
        "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-300",
    };
  }

  return {
    text: labels.pending,
    icon: <AlertCircle className="h-3.5 w-3.5 text-amber-600" />,
    className:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300",
  };
};
